import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { usePortalBrand } from '../../contexts/PortalBrandContext';

const PortalBrandSwitcher: React.FC = () => {
  const { brands, selectedBrand, setSelectedBrandId } = usePortalBrand();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (brands.length === 0) return null;

  if (brands.length === 1) {
    return (
      <span className="text-[11px] font-semibold text-gray-500 whitespace-nowrap">
        {selectedBrand?.displayName ?? brands[0].displayName}
      </span>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="Switch brand"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy rounded"
      >
        <span className="whitespace-nowrap">{selectedBrand?.displayName ?? 'Select brand'}</span>
        <ChevronDownIcon className="h-3 w-3" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 z-50 w-56 rounded-lg border border-gray-200 bg-white shadow-lg py-1"
        >
          {brands.map((b) => (
            <button
              key={b.id}
              role="menuitem"
              onClick={() => {
                setSelectedBrandId(b.id);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <span>{b.displayName}</span>
              {selectedBrand?.id === b.id && (
                <CheckIcon className="h-4 w-4 text-indigo-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalBrandSwitcher;
